import React, { useCallback, useEffect, useState } from "react";
import { Divider, Switch, Button, Alert } from "antd";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  ReloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import IBAccountDetails from "components/UserSettings/IBAccountDetails";
import IBPortfolio from "components/UserSettings/IBPortfolio";
import { apiGetIBAccountDetails } from "api/apiTws";
import OrdersStatus from "components/OrdersStatus/OrdersStatus";
import { Link } from "react-router-dom";
import { paths } from "utils/constants";
import { useTradingRefresh } from "components/Trading/tradingRefresh";

// IBKR account details + portfolio ({connected: false} when the gateway isn't
// reachable), reloaded on the Trading page's refresh (the page itself passes
// its refreshKey: it sits outside its own refresh context). Not fetched while
// enabled is false.
export const useIbAccountDetails = ({ refreshKey: pageKey, enabled = true } = {}) => {
  const { refreshKey: contextKey } = useTradingRefresh();
  const refreshKey = pageKey ?? contextKey;
  const [ibAccountDetails, setIbAccountDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchIbAccountDetails = useCallback(async () => {
    try {
      setLoading(true);
      setIbAccountDetails(await apiGetIBAccountDetails());
    } catch (err) {
      console.error("Failed to fetch IB account details:", err);
      setIbAccountDetails(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) fetchIbAccountDetails();
  }, [fetchIbAccountDetails, refreshKey, enabled]);

  return { ibAccountDetails, loading, refresh: fetchIbAccountDetails };
};

// account: useIbAccountDetails(). Trading page tab (embedded): no title and
// no orders (they have their own tab); the page fetches the account itself,
// since it shows the tab only while IBKR is connected.
export const IBAccountSection = ({ account, embedded = false }) => {
  const { ibAccountDetails, loading, refresh } = account;
  const [showSensitiveValues, setShowSensitiveValues] = useState(() => {
    const stored = localStorage.getItem("ibAccountShowSensitiveValues");
    return stored ? JSON.parse(stored) : false;
  });

  useEffect(() => {
    localStorage.setItem(
      "ibAccountShowSensitiveValues",
      JSON.stringify(showSensitiveValues)
    );
  }, [showSensitiveValues]);

  const handleToggleSensitiveValues = (checked) => {
    setShowSensitiveValues(checked);
  };

  const handleRefresh = () => {
    refresh();
  };

  return (
    <>
      {ibAccountDetails?.connected === false && (
        <Alert
          message="IBKR account not connected"
          type="error"
          action={
            <Button type="link" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      )}
      {ibAccountDetails?.connected === true && (
        <>
          {!embedded && (
            <Divider orientation="left">Interactive Brokers Integration</Divider>
          )}
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Show sensitive values:</span>
              <Switch
                checked={showSensitiveValues}
                onChange={handleToggleSensitiveValues}
                checkedChildren={<EyeOutlined />}
                unCheckedChildren={<EyeInvisibleOutlined />}
              />
            </div>
            <div className="flex items-center gap-2">
              <Link to={paths.userSettings}>
                <Button type="link" icon={<SettingOutlined />}>
                  Settings
                </Button>
              </Link>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              >
                Refresh Data
              </Button>
            </div>
          </div>
          <div className="cards-wrapper">
            {ibAccountDetails?.accountDetails && (
              <IBAccountDetails
                accountData={ibAccountDetails.accountDetails}
                showSensitiveValues={showSensitiveValues}
              />
            )}
            {ibAccountDetails?.portfolio && (
              <IBPortfolio
                portfolioData={ibAccountDetails.portfolio}
                showSensitiveValues={showSensitiveValues}
              />
            )}
          </div>
        </>
      )}
      {!embedded && <OrdersStatus />}
    </>
  );
};

// Home: the whole section
const IBIntergration = () => <IBAccountSection account={useIbAccountDetails()} />;

export default IBIntergration;
